import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Plus, Loader2, X } from 'lucide-react';

const MyLeaves = () => {
  const { user } = useAuth();
  const { dialogProps, confirm } = useConfirmation();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    is_half_day: false,
    half_day_type: '',
    reason: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [typesRes, balanceRes, requestsRes] = await Promise.all([
        leaveAPI.getTypes(),
        leaveAPI.getBalance(user.employee_id),
        leaveAPI.getRequests({ employee_id: user.employee_id }),
      ]);
      setLeaveTypes(typesRes.data);
      setLeaveBalance(balanceRes.data);
      setRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to fetch leave data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    setApplyLoading(true);
    try {
      await leaveAPI.createRequest({
        ...formData,
        employee_id: user.employee_id,
        company_id: user.company_id,
      });
      toast.success('Leave request submitted');
      setIsApplyOpen(false);
      setFormData({
        leave_type_id: '',
        start_date: '',
        end_date: '',
        is_half_day: false,
        half_day_type: '',
        reason: '',
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply leave');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleCancel = async (requestId) => {
    const confirmed = await confirm({
      title: 'Cancel Leave Request',
      description: 'Are you sure you want to cancel this leave request?',
      confirmText: 'Cancel Request',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await leaveAPI.cancelRequest(requestId);
      toast.success('Leave request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    APPROVED_BY_MANAGER: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-gray-100 text-gray-800',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Leaves</h1>
          <p className="text-muted-foreground">Apply for leave and view your leave balance</p>
        </div>
        <Dialog open={isApplyOpen} onOpenChange={setIsApplyOpen}>
          <DialogTrigger asChild>
            <Button data-testid="apply-leave-btn">
              <Plus className="mr-2 h-4 w-4" />
              Apply Leave
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Apply for Leave</DialogTitle>
              <DialogDescription>
                Submit a new leave request
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleApplyLeave}>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label>Leave Type *</Label>
                  <Select
                    value={formData.leave_type_id}
                    onValueChange={(v) => setFormData({ ...formData, leave_type_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {leaveTypes.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name} ({type.code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_half_day"
                      checked={formData.is_half_day}
                      onChange={(e) => setFormData({ ...formData, is_half_day: e.target.checked })}
                    />
                    <Label htmlFor="is_half_day">Half Day</Label>
                  </div>
                  {formData.is_half_day && (
                    <Select
                      value={formData.half_day_type}
                      onValueChange={(v) => setFormData({ ...formData, half_day_type: v })}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Select half" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FIRST_HALF">First Half</SelectItem>
                        <SelectItem value="SECOND_HALF">Second Half</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Enter reason for leave"
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsApplyOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={applyLoading || !formData.leave_type_id}>
                  {applyLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Submit
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Leave Balance */}
      {leaveBalance && (
        <Card>
          <CardHeader>
            <CardTitle>Leave Balance {leaveBalance.year}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
              {leaveBalance.balances?.map((balance) => (
                <div key={balance.leave_type_id} className="p-4 rounded-lg border">
                  <h4 className="font-medium text-sm">{balance.leave_type_name}</h4>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="text-2xl font-bold">{balance.balance}</span>
                    <span className="text-sm text-muted-foreground mb-0.5">/ {balance.allowed}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {balance.used} used
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leave Requests */}
      <Card>
        <CardHeader>
          <CardTitle>My Leave Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Leave Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Applied On</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No leave requests found
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell className="font-medium">{request.leave_type_name}</TableCell>
                    <TableCell>{new Date(request.start_date).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(request.end_date).toLocaleDateString()}</TableCell>
                    <TableCell>{request.days_count}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[request.status]}>
                        {request.status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(request.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {['PENDING', 'APPROVED_BY_MANAGER'].includes(request.status) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => handleCancel(request.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <ConfirmationDialog {...dialogProps} />
    </div>
  );
};

export default MyLeaves;
